import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, interval } from 'rxjs';
import { ActiveCallRateLimit, CallRateLimitService, formatRetryAfter } from '../../core/call-rate-limit/call-rate-limit.service';
import { PhoneNumberPipe } from '../pipes/phone-number.pipe';

/**
 * Tells the owner that one of their own numbers hit the test call limit and
 * when calls from it resume. Rendered as a full-width bar in the dashboard
 * shell ("top") or inside the guided-setup test step ("inline").
 */
@Component({
  selector: 'rosie-call-rate-limit-banner',
  templateUrl: './call-rate-limit-banner.component.html',
  styleUrls: ['./call-rate-limit-banner.component.scss'],
  standalone: false,
})
export class CallRateLimitBannerComponent implements OnInit, OnDestroy {
  @Input() variant: 'top' | 'inline' = 'top';

  private readonly callRateLimits = inject(CallRateLimitService);
  private readonly translate = inject(TranslateService);
  private readonly phoneNumberPipe = inject(PhoneNumberPipe);
  private readonly changeDetector = inject(ChangeDetectorRef);

  private tickSubscription: Subscription;

  ngOnInit(): void {
    this.tickSubscription = interval(1000).subscribe(() => {
      this.callRateLimits.pruneExpired();
      this.changeDetector.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.tickSubscription?.unsubscribe();
  }

  get limits(): ActiveCallRateLimit[] {
    return this.callRateLimits.activeLimits;
  }

  message(limit: ActiveCallRateLimit): string {
    const duration = formatRetryAfter(limit.expiresAt);
    return this.translate.instant('CALL_RATE_LIMIT.BANNER_TEXT', {
      number: this.phoneNumberPipe.transform(limit.number),
      duration: this.translate.instant(duration.key, { count: duration.count }),
    });
  }

  trackByNumber(_index: number, limit: ActiveCallRateLimit): string {
    return limit.number;
  }
}
